import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Plus, Search, ShoppingCart, Trash2, Edit, Check, X, 
    Clock, User, MapPin, Truck, Users, Calendar, AlertCircle,
    CheckCircle, XCircle, FileText, Download
} from 'lucide-react';
import CagnotteWidget from '../components/CagnotteWidget';

const ShoppingListsPageV2 = () => {
    const { api, user, hasPermission } = useAuth();
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
    const [selectedList, setSelectedList] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [shoppers, setShoppers] = useState([]);
    const [sectors, setSectors] = useState([]);

    // Form state for new list
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        shopping_date: '',
        shopper_id: '',
        transport_cost: 0,
        labor_cost: 0,
        amount_given: 0,
        sector_filter: '',
        items: []
    });

    // Validation form
    const [validationData, setValidationData] = useState({
        action: '',
        notes: '',
        amount_given: 0
    });

    // Complete form (for returning money)
    const [completeData, setCompleteData] = useState({
        amount_returned: 0,
        notes: ''
    });

    // New item form
    const [newItem, setNewItem] = useState({
        name: '',
        quantity: 1,
        unit: 'unité',
        unit_price: '',
        supplier: '',
        purchase_location: '',
        sector: ''
    });

    const canValidate = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR';
    const canFinalValidate = user?.role === 'ADMIN'; // Seul Admin peut faire validation finale
    const canCreate = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';
    
    // Déterminer les actions possibles selon le rôle et le statut
    const getAvailableActions = (list) => {
        const status = list?.status;
        const role = user?.role;
        const actions = [];
        
        // Statuts en attente de validation Directeur
        const pendingDirector = ['en_attente_directeur', 'demandee', 'pending'];
        // Statuts en attente de validation Admin
        const pendingAdmin = ['valide_directeur', 'en_attente_admin'];
        // Tous les statuts en attente
        const allPending = [...pendingDirector, ...pendingAdmin];
        
        if (pendingDirector.includes(status)) {
            if (role === 'DIRECTEUR' || role === 'ADMIN') {
                actions.push({ action: 'validate_director', label: 'Valider (Directeur)', color: 'bg-blue-600' });
            }
            if (role === 'ADMIN') {
                actions.push({ action: 'validate_admin', label: 'Valider (Final)', color: 'bg-emerald-600' });
            }
            if (role === 'DIRECTEUR' || role === 'ADMIN') {
                actions.push({ action: 'reject', label: 'Refuser', color: 'bg-red-600' });
            }
        }
        
        if (pendingAdmin.includes(status)) {
            if (role === 'ADMIN') {
                actions.push({ action: 'validate_admin', label: 'Valider (Final)', color: 'bg-emerald-600' });
                actions.push({ action: 'reject', label: 'Refuser', color: 'bg-red-600' });
            }
        }
        
        // Statuts validés → démarrer achat
        if (['valide_admin', 'validee', 'validated'].includes(status)) {
            actions.push({ action: 'start_purchase', label: 'Démarrer achat', color: 'bg-indigo-600' });
        }
        
        // En cours → marquer acheté
        if (['en_cours'].includes(status)) {
            actions.push({ action: 'mark_purchased', label: 'Marquer acheté', color: 'bg-purple-600' });
        }
        
        // Acheté → marquer reçu
        if (['achetee'].includes(status)) {
            actions.push({ action: 'mark_received', label: 'Marquer reçu', color: 'bg-teal-600' });
        }
        
        // Refusé → réouvrir
        if (['refusee', 'rejected'].includes(status)) {
            actions.push({ action: 'reopen', label: 'Réouvrir', color: 'bg-gray-600' });
        }
        
        return actions;
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [listsRes, shoppersRes, sectorsRes] = await Promise.all([
                api().get('/shopping-lists'),
                api().get('/shopping-lists/shoppers'),
                api().get('/shopping-lists/sectors')
            ]);
            setLists(listsRes.data);
            setShoppers(shoppersRes.data);
            setSectors(sectorsRes.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateList = async (e) => {
        e.preventDefault();
        
        // Validation: vérifier que la liste n'est pas vide
        if (!formData.items || formData.items.length === 0) {
            toast.error('Ajoutez au moins un article à la liste');
            return;
        }
        
        // Validation: vérifier le nom
        if (!formData.name || formData.name.trim() === '') {
            toast.error('Le nom de la liste est requis');
            return;
        }
        
        try {
            // Préparer les données avec conversion propre des nombres
            const cleanedItems = formData.items.map(item => ({
                name: item.name || '',
                quantity: parseFloat(item.quantity) || 1,
                unit: item.unit || 'unité',
                unit_price: parseFloat(item.unit_price) || 0,
                supplier: item.supplier || '',
                purchase_location: item.purchase_location || '',
                sector: item.sector || ''
            }));
            
            const payload = {
                name: formData.name.trim(),
                description: formData.description || '',
                shopping_date: formData.shopping_date || null,
                shopper_id: formData.shopper_id || null,
                transport_cost: parseFloat(formData.transport_cost) || 0,
                labor_cost: parseFloat(formData.labor_cost) || 0,
                amount_given: parseFloat(formData.amount_given) || 0,
                sector_filter: formData.sector_filter || null,
                items: cleanedItems
            };
            
            // Logs temporaires pour debug
            console.log('[Shopping] Payload envoyé:', payload);
            console.log('[Shopping] Items:', cleanedItems);
            console.log('[Shopping] Total articles:', cleanedItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0));
            
            const response = await api().post('/shopping-lists', payload);
            console.log('[Shopping] Réponse backend:', response.data);
            
            setLists([response.data, ...lists]);
            toast.success('Liste créée avec succès');
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            console.error('[Shopping] Erreur création:', error);
            console.error('[Shopping] Détail erreur:', error.response?.data);
            const errorMsg = error.response?.data?.detail || 
                            (typeof error.response?.data === 'string' ? error.response?.data : 
                            'Erreur lors de la création de la liste');
            toast.error(errorMsg);
        }
    };

    const handleAddItem = () => {
        if (!newItem.name) {
            toast.error('Le nom de l\'article est requis');
            return;
        }
        setFormData({
            ...formData,
            items: [...formData.items, { ...newItem, id: Date.now().toString() }]
        });
        setNewItem({
            name: '',
            quantity: 1,
            unit: 'unité',
            unit_price: '',
            supplier: '',
            purchase_location: '',
            sector: ''
        });
    };

    const handleRemoveItem = (itemId) => {
        setFormData({
            ...formData,
            items: formData.items.filter(item => item.id !== itemId)
        });
    };

    const handleValidation = async (action) => {
        if (!selectedList) return;
        
        try {
            await api().post(`/shopping-lists/${selectedList.id}/validate`, {
                action,
                notes: validationData.notes,
                amount_given: validationData.amount_given || selectedList.amount_given
            });
            
            toast.success(
                action === 'validate' ? 'Liste validée' : 
                action === 'reject' ? 'Liste rejetée' : 'Liste modifiée'
            );
            setValidationDialogOpen(false);
            setValidationData({ action: '', notes: '', amount_given: 0 });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        }
    };

    const handleDeleteList = async (listId) => {
        if (!window.confirm('Supprimer cette liste ?')) return;
        
        try {
            await api().delete(`/shopping-lists/${listId}`);
            setLists(lists.filter(l => l.id !== listId));
            toast.success('Liste supprimée');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const handleCompleteList = async () => {
        if (!selectedList) return;
        
        try {
            await api().post(`/shopping-lists/${selectedList.id}/complete`, {
                amount_returned: completeData.amount_returned,
                notes: completeData.notes
            });
            
            toast.success('Liste terminée - Reste rendu enregistré');
            setCompleteDialogOpen(false);
            setCompleteData({ amount_returned: 0, notes: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            shopping_date: '',
            shopper_id: '',
            transport_cost: 0,
            labor_cost: 0,
            amount_given: 0,
            sector_filter: '',
            items: []
        });
        setNewItem({
            name: '',
            quantity: 1,
            unit: 'unité',
            unit_price: '',
            supplier: '',
            purchase_location: '',
            sector: ''
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            // Nouveaux statuts hiérarchiques
            brouillon: { color: 'bg-gray-500', label: 'Brouillon', icon: Clock },
            en_attente_directeur: { color: 'bg-yellow-500', label: 'Attente Directeur', icon: Clock },
            valide_directeur: { color: 'bg-blue-500', label: 'Validé Directeur', icon: CheckCircle },
            en_attente_admin: { color: 'bg-orange-500', label: 'Attente Admin', icon: Clock },
            valide_admin: { color: 'bg-emerald-600', label: 'Validé Admin ✓', icon: CheckCircle },
            refusee: { color: 'bg-red-500', label: 'Refusée', icon: XCircle },
            en_cours: { color: 'bg-indigo-500', label: 'Achat en cours', icon: Clock },
            achetee: { color: 'bg-purple-500', label: 'Achetée', icon: Check },
            receptionnee: { color: 'bg-teal-500', label: 'Réceptionnée', icon: CheckCircle },
            archivee: { color: 'bg-gray-400', label: 'Archivée', icon: FileText },
            // Legacy statuts
            pending: { color: 'bg-yellow-500', label: 'En attente', icon: Clock },
            demandee: { color: 'bg-yellow-500', label: 'En attente', icon: Clock },
            validated: { color: 'bg-green-500', label: 'Validée', icon: CheckCircle },
            validee: { color: 'bg-green-500', label: 'Validée', icon: CheckCircle },
            rejected: { color: 'bg-red-500', label: 'Rejetée', icon: XCircle },
            completed: { color: 'bg-blue-500', label: 'Terminée', icon: Check }
        };
        const config = statusConfig[status] || statusConfig.pending;
        const Icon = config.icon;
        
        return (
            <Badge className={`${config.color} text-white flex items-center gap-1`}>
                <Icon className="w-3 h-3" />
                {config.label}
            </Badge>
        );
    };

    const filteredLists = lists.filter(list => {
        const matchesSearch = list.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || list.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const pendingCount = lists.filter(l => l.status === 'pending').length;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="shopping-lists-page">
            {/* Cagnotte Widget - Always visible at top */}
            <CagnotteWidget onExpenseAdded={fetchData} />

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Listes de Courses</h1>
                    <p className="text-muted-foreground">Gestion des achats et approvisionnements</p>
                </div>
                <div className="flex gap-2">
                    {canValidate && pendingCount > 0 && (
                        <Badge variant="destructive" className="px-3 py-1">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {pendingCount} en attente de validation
                        </Badge>
                    )}
                    {canCreate && (
                        <Button 
                            onClick={() => { resetForm(); setDialogOpen(true); }}
                            data-testid="add-shopping-list-btn"
                            className="bg-green-600 hover:bg-green-700"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle Liste
                        </Button>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Listes</p>
                                <p className="text-2xl font-bold">{lists.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                                <Clock className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">En Attente</p>
                                <p className="text-2xl font-bold">{pendingCount}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Validées</p>
                                <p className="text-2xl font-bold">{lists.filter(l => l.status === 'validated').length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                                <Check className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Terminées</p>
                                <p className="text-2xl font-bold">{lists.filter(l => l.status === 'completed').length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Rechercher une liste..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className="w-full sm:w-48">
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                <SelectItem value="pending">En attente</SelectItem>
                                <SelectItem value="validated">Validées</SelectItem>
                                <SelectItem value="rejected">Rejetées</SelectItem>
                                <SelectItem value="completed">Terminées</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Lists */}
            <div className="grid gap-4">
                {filteredLists.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center text-muted-foreground">
                            <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucune liste de courses trouvée</p>
                        </CardContent>
                    </Card>
                ) : (
                    filteredLists.map(list => (
                        <Card key={list.id} className="hover:shadow-md transition-shadow">
                            <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            {list.name}
                                            {getStatusBadge(list.status)}
                                        </CardTitle>
                                        <CardDescription className="mt-1">
                                            Créée par {list.created_by_name} le {new Date(list.created_at).toLocaleDateString('fr-FR')}
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => { setSelectedList(list); setDetailsDialogOpen(true); }}
                                        >
                                            <FileText className="w-4 h-4 mr-1" />
                                            Détails
                                        </Button>
                                        {canValidate && list.status === 'pending' && (
                                            <Button
                                                size="sm"
                                                className="bg-green-600 hover:bg-green-700"
                                                onClick={() => { 
                                                    setSelectedList(list); 
                                                    setValidationData({ ...validationData, amount_given: list.amount_given || 0 });
                                                    setValidationDialogOpen(true); 
                                                }}
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                Valider
                                            </Button>
                                        )}
                                        {list.status === 'validated' && (
                                            <Button
                                                size="sm"
                                                className="bg-blue-600 hover:bg-blue-700"
                                                onClick={() => { 
                                                    setSelectedList(list); 
                                                    setCompleteDialogOpen(true); 
                                                }}
                                            >
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                Terminer
                                            </Button>
                                        )}
                                        {(user?.id === list.created_by || user?.role === 'ADMIN') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700"
                                                onClick={() => handleDeleteList(list.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                    <div>
                                        <p className="text-muted-foreground">Articles</p>
                                        <p className="font-semibold">{list.total_items}</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Total Articles</p>
                                        <p className="font-semibold">{(list.items_total || 0).toLocaleString()} FCFA</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Total Général</p>
                                        <p className="font-semibold text-emerald-600">{(list.grand_total || 0).toLocaleString()} FCFA</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Montant Donné</p>
                                        <p className="font-semibold">{(list.amount_given || 0).toLocaleString()} FCFA</p>
                                    </div>
                                    <div>
                                        <p className="text-muted-foreground">Date Courses</p>
                                        <p className="font-semibold">
                                            {list.shopping_date ? new Date(list.shopping_date).toLocaleDateString('fr-FR') : '-'}
                                        </p>
                                    </div>
                                </div>
                                {list.shopper_name && (
                                    <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                                        <User className="w-4 h-4" />
                                        Responsable: <span className="font-medium text-foreground">{list.shopper_name}</span>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Create List Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nouvelle Liste de Courses</DialogTitle>
                        <DialogDescription>
                            Créez une liste détaillée avec tous les articles nécessaires
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateList} className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Nom de la liste *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ex: Courses semaine 12"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="shopping_date">Date des courses</Label>
                                <Input
                                    id="shopping_date"
                                    type="date"
                                    value={formData.shopping_date}
                                    onChange={(e) => setFormData({ ...formData, shopping_date: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="shopper">Responsable des courses</Label>
                                <Select
                                    value={formData.shopper_id || "none"}
                                    onValueChange={(val) => setFormData({ ...formData, shopper_id: val === "none" ? "" : val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Non assigné</SelectItem>
                                        {shoppers.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="sector">Secteur</Label>
                                <Select
                                    value={formData.sector_filter || "all"}
                                    onValueChange={(val) => setFormData({ ...formData, sector_filter: val === "all" ? "" : val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Tous secteurs" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous secteurs</SelectItem>
                                        {sectors.map(s => (
                                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Notes ou instructions..."
                                rows={2}
                            />
                        </div>

                        {/* Costs */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="transport_cost">Transport (FCFA)</Label>
                                <Input
                                    id="transport_cost"
                                    type="number"
                                    min="0"
                                    value={formData.transport_cost}
                                    onChange={(e) => setFormData({ ...formData, transport_cost: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="labor_cost">Main d'oeuvre (FCFA)</Label>
                                <Input
                                    id="labor_cost"
                                    type="number"
                                    min="0"
                                    value={formData.labor_cost}
                                    onChange={(e) => setFormData({ ...formData, labor_cost: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="amount_given">Montant demandé (FCFA)</Label>
                                <Input
                                    id="amount_given"
                                    type="number"
                                    min="0"
                                    value={formData.amount_given}
                                    onChange={(e) => setFormData({ ...formData, amount_given: parseFloat(e.target.value) || 0 })}
                                />
                            </div>
                        </div>

                        {/* Items Section */}
                        <div className="border rounded-lg p-4 space-y-4">
                            <h3 className="font-semibold flex items-center gap-2">
                                <ShoppingCart className="w-5 h-5" />
                                Articles
                            </h3>
                            
                            {/* Add Item Form */}
                            <div className="grid grid-cols-7 gap-2 items-end bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                                <div className="col-span-2">
                                    <Label className="text-xs">Nom *</Label>
                                    <Input
                                        value={newItem.name}
                                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                                        placeholder="Article"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Qté</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={newItem.quantity}
                                        onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Prix unit.</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={newItem.unit_price}
                                        onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })}
                                        placeholder="FCFA"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Fournisseur</Label>
                                    <Input
                                        value={newItem.supplier}
                                        onChange={(e) => setNewItem({ ...newItem, supplier: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Lieu</Label>
                                    <Input
                                        value={newItem.purchase_location}
                                        onChange={(e) => setNewItem({ ...newItem, purchase_location: e.target.value })}
                                    />
                                </div>
                                <Button type="button" onClick={handleAddItem} size="sm">
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>

                            {/* Items List */}
                            {formData.items.length > 0 && (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Article</TableHead>
                                            <TableHead className="text-center">Qté</TableHead>
                                            <TableHead className="text-right">Prix Unit.</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                            <TableHead>Fournisseur</TableHead>
                                            <TableHead>Lieu</TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {formData.items.map(item => (
                                            <TableRow key={item.id}>
                                                <TableCell className="font-medium">{item.name}</TableCell>
                                                <TableCell className="text-center">{item.quantity} {item.unit}</TableCell>
                                                <TableCell className="text-right">{(item.unit_price || 0).toLocaleString()} FCFA</TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    {((item.unit_price || 0) * (item.quantity || 1)).toLocaleString()} FCFA
                                                </TableCell>
                                                <TableCell>{item.supplier || '-'}</TableCell>
                                                <TableCell>{item.purchase_location || '-'}</TableCell>
                                                <TableCell>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveItem(item.id)}
                                                        className="text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow className="bg-gray-50 dark:bg-gray-800">
                                            <TableCell colSpan={3} className="font-semibold">Total Articles</TableCell>
                                            <TableCell className="text-right font-bold text-emerald-600">
                                                {formData.items.reduce((sum, i) => sum + ((i.unit_price || 0) * (i.quantity || 1)), 0).toLocaleString()} FCFA
                                            </TableCell>
                                            <TableCell colSpan={3}></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            )}
                        </div>

                        {/* Summary */}
                        <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                            <CardContent className="pt-4">
                                <div className="grid grid-cols-4 gap-4 text-center">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Articles</p>
                                        <p className="text-lg font-bold">
                                            {formData.items.reduce((sum, i) => sum + ((i.unit_price || 0) * (i.quantity || 1)), 0).toLocaleString()} FCFA
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Transport</p>
                                        <p className="text-lg font-bold">{(formData.transport_cost || 0).toLocaleString()} FCFA</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Main d'oeuvre</p>
                                        <p className="text-lg font-bold">{(formData.labor_cost || 0).toLocaleString()} FCFA</p>
                                    </div>
                                    <div className="border-l-2 border-emerald-300">
                                        <p className="text-sm text-muted-foreground">TOTAL</p>
                                        <p className="text-xl font-bold text-emerald-600">
                                            {(
                                                formData.items.reduce((sum, i) => sum + ((i.unit_price || 0) * (i.quantity || 1)), 0) +
                                                (formData.transport_cost || 0) +
                                                (formData.labor_cost || 0)
                                            ).toLocaleString()} FCFA
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={formData.items.length === 0}>
                                Créer la liste
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Details Dialog */}
            <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {selectedList?.name}
                            {selectedList && getStatusBadge(selectedList.status)}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedList && (
                        <div className="space-y-4">
                            {/* Info */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-muted-foreground">Créée par</p>
                                    <p className="font-medium">{selectedList.created_by_name}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Date création</p>
                                    <p className="font-medium">{new Date(selectedList.created_at).toLocaleDateString('fr-FR')}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Date courses</p>
                                    <p className="font-medium">
                                        {selectedList.shopping_date ? new Date(selectedList.shopping_date).toLocaleDateString('fr-FR') : '-'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Responsable</p>
                                    <p className="font-medium">{selectedList.shopper_name || '-'}</p>
                                </div>
                            </div>

                            {/* Items */}
                            <div>
                                <h4 className="font-semibold mb-2">Articles ({selectedList.total_items})</h4>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Article</TableHead>
                                            <TableHead className="text-center">Qté</TableHead>
                                            <TableHead className="text-right">Prix Unit.</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                            <TableHead>Fournisseur</TableHead>
                                            <TableHead>Lieu</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(selectedList.items || []).map(item => (
                                            <TableRow key={item.id}>
                                                <TableCell className="font-medium">{item.name}</TableCell>
                                                <TableCell className="text-center">{item.quantity} {item.unit}</TableCell>
                                                <TableCell className="text-right">{(item.unit_price || 0).toLocaleString()}</TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    {((item.unit_price || 0) * (item.quantity || 1)).toLocaleString()}
                                                </TableCell>
                                                <TableCell>{item.supplier || '-'}</TableCell>
                                                <TableCell>{item.purchase_location || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {/* Totals */}
                            <Card className="bg-gray-50 dark:bg-gray-800">
                                <CardContent className="pt-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span>Total Articles</span>
                                            <span className="font-semibold">{(selectedList.items_total || 0).toLocaleString()} FCFA</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Transport</span>
                                            <span className="font-semibold">{(selectedList.transport_cost || 0).toLocaleString()} FCFA</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Main d'oeuvre</span>
                                            <span className="font-semibold">{(selectedList.labor_cost || 0).toLocaleString()} FCFA</span>
                                        </div>
                                        <div className="flex justify-between text-lg border-t pt-2">
                                            <span className="font-bold">Total Général</span>
                                            <span className="font-bold text-emerald-600">{(selectedList.grand_total || 0).toLocaleString()} FCFA</span>
                                        </div>
                                        <div className="flex justify-between border-t pt-2">
                                            <span>Montant Donné</span>
                                            <span className="font-semibold">{(selectedList.amount_given || 0).toLocaleString()} FCFA</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Reste Rendu</span>
                                            <span className="font-semibold">{(selectedList.amount_returned || 0).toLocaleString()} FCFA</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Historique de validation - Système hiérarchique à 3 niveaux */}
                            <Card className="border-2 border-gray-200 dark:border-gray-700">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                                        Historique de validation
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    {/* Niveau 1: Créateur */}
                                    <div className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                                        <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-bold">1</div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">Créé par</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {selectedList.validation?.created_by_name || selectedList.created_by_name || 'Inconnu'}
                                                {selectedList.validation?.created_by_role && ` (${selectedList.validation.created_by_role})`}
                                            </p>
                                            {(selectedList.validation?.created_at || selectedList.created_at) && (
                                                <p className="text-xs text-gray-500">
                                                    {new Date(selectedList.validation?.created_at || selectedList.created_at).toLocaleString('fr-FR')}
                                                </p>
                                            )}
                                        </div>
                                        <CheckCircle className="w-5 h-5 text-gray-400" />
                                    </div>
                                    
                                    {/* Niveau 2: Validation Directeur */}
                                    <div className={`flex items-start gap-3 p-2 rounded-lg ${
                                        selectedList.validation?.validated_by_director_name 
                                            ? 'bg-blue-50 dark:bg-blue-900/30' 
                                            : 'bg-gray-50 dark:bg-gray-800 opacity-50'
                                    }`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                            selectedList.validation?.validated_by_director_name ? 'bg-blue-500' : 'bg-gray-300'
                                        }`}>2</div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">Validation Directeur</p>
                                            {selectedList.validation?.validated_by_director_name ? (
                                                <>
                                                    <p className="text-sm text-blue-600 dark:text-blue-400">
                                                        ✓ Par {selectedList.validation.validated_by_director_name}
                                                    </p>
                                                    {selectedList.validation.validated_by_director_at && (
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(selectedList.validation.validated_by_director_at).toLocaleString('fr-FR')}
                                                        </p>
                                                    )}
                                                    {selectedList.validation.director_notes && (
                                                        <p className="text-xs italic text-gray-500 mt-1">"{selectedList.validation.director_notes}"</p>
                                                    )}
                                                </>
                                            ) : (
                                                <p className="text-sm text-gray-500">En attente...</p>
                                            )}
                                        </div>
                                        {selectedList.validation?.validated_by_director_name ? (
                                            <CheckCircle className="w-5 h-5 text-blue-500" />
                                        ) : (
                                            <Clock className="w-5 h-5 text-gray-300" />
                                        )}
                                    </div>
                                    
                                    {/* Niveau 3: Validation Admin (Final) */}
                                    <div className={`flex items-start gap-3 p-2 rounded-lg ${
                                        selectedList.validation?.validated_by_admin_name 
                                            ? 'bg-emerald-50 dark:bg-emerald-900/30' 
                                            : 'bg-gray-50 dark:bg-gray-800 opacity-50'
                                    }`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                            selectedList.validation?.validated_by_admin_name ? 'bg-emerald-500' : 'bg-gray-300'
                                        }`}>3</div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">Validation Admin (Final)</p>
                                            {selectedList.validation?.validated_by_admin_name ? (
                                                <>
                                                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                                                        ✓ Par {selectedList.validation.validated_by_admin_name}
                                                    </p>
                                                    {selectedList.validation.validated_by_admin_at && (
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(selectedList.validation.validated_by_admin_at).toLocaleString('fr-FR')}
                                                        </p>
                                                    )}
                                                    {selectedList.validation.admin_notes && (
                                                        <p className="text-xs italic text-gray-500 mt-1">"{selectedList.validation.admin_notes}"</p>
                                                    )}
                                                </>
                                            ) : (
                                                <p className="text-sm text-gray-500">En attente...</p>
                                            )}
                                        </div>
                                        {selectedList.validation?.validated_by_admin_name ? (
                                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                                        ) : (
                                            <Clock className="w-5 h-5 text-gray-300" />
                                        )}
                                    </div>
                                    
                                    {/* Si refusé */}
                                    {selectedList.validation?.rejected_by_name && (
                                        <div className="flex items-start gap-3 p-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                                            <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center">
                                                <XCircle className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-red-700 dark:text-red-300">Refusée</p>
                                                <p className="text-sm text-red-600 dark:text-red-400">
                                                    Par {selectedList.validation.rejected_by_name} ({selectedList.validation.rejected_by_role})
                                                </p>
                                                {selectedList.validation.rejected_at && (
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(selectedList.validation.rejected_at).toLocaleString('fr-FR')}
                                                    </p>
                                                )}
                                                {selectedList.validation.rejection_reason && (
                                                    <p className="text-sm mt-1 p-2 bg-white dark:bg-gray-900 rounded border">
                                                        {selectedList.validation.rejection_reason}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                            
                            {/* Actions disponibles selon le rôle */}
                            {getAvailableActions(selectedList).length > 0 && (
                                <div className="flex flex-wrap gap-2 pt-4 border-t">
                                    {getAvailableActions(selectedList).map(action => (
                                        <Button
                                            key={action.action}
                                            className={`${action.color} text-white`}
                                            onClick={() => {
                                                setValidationData({ ...validationData, action: action.action });
                                                handleValidation(action.action);
                                            }}
                                        >
                                            {action.label}
                                        </Button>
                                    ))}
                                </div>
                            )}
                            
                            {/* Export Buttons */}
                            <div className="flex gap-2 pt-4 border-t">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/shopping-lists/${selectedList.id}/pdf`;
                                        window.open(url, '_blank');
                                    }}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Exporter PDF
                                </Button>
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => {
                                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/exports/shopping-lists/${selectedList.id}/excel`;
                                        window.open(url, '_blank');
                                    }}
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Exporter Excel
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Validation Dialog */}
            <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Validation de la Liste</DialogTitle>
                        <DialogDescription>
                            {selectedList?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Montant à donner (FCFA)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={validationData.amount_given}
                                onChange={(e) => setValidationData({ ...validationData, amount_given: parseFloat(e.target.value) || 0 })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={validationData.notes}
                                onChange={(e) => setValidationData({ ...validationData, notes: e.target.value })}
                                placeholder="Remarques ou instructions..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => handleValidation('reject')}
                            className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                        >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rejeter
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => handleValidation('modify')}
                            className="flex-1"
                        >
                            <Edit className="w-4 h-4 mr-1" />
                            Modifier
                        </Button>
                        <Button
                            onClick={() => handleValidation('validate')}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Valider
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Complete Shopping Dialog */}
            <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                            Terminer les Courses
                        </DialogTitle>
                        <DialogDescription>
                            {selectedList?.name}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedList && (
                        <div className="space-y-4">
                            {/* Summary */}
                            <Card className="bg-gray-50 dark:bg-gray-800">
                                <CardContent className="pt-4 space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>Montant donné</span>
                                        <span className="font-semibold">{(selectedList.amount_given || 0).toLocaleString()} FCFA</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Total dépensé</span>
                                        <span className="font-semibold text-red-600">- {(selectedList.grand_total || 0).toLocaleString()} FCFA</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-2">
                                        <span className="font-medium">Reste attendu</span>
                                        <span className="font-bold text-emerald-600">
                                            {((selectedList.amount_given || 0) - (selectedList.grand_total || 0)).toLocaleString()} FCFA
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>

                            <div className="space-y-2">
                                <Label htmlFor="amount_returned">Reste rendu (FCFA) *</Label>
                                <Input
                                    id="amount_returned"
                                    type="number"
                                    min="0"
                                    value={completeData.amount_returned}
                                    onChange={(e) => setCompleteData({ ...completeData, amount_returned: parseFloat(e.target.value) || 0 })}
                                    placeholder="Montant rendu"
                                />
                            </div>

                            {/* Difference alert */}
                            {completeData.amount_returned > 0 && (
                                <div className={`p-3 rounded-lg text-sm ${
                                    completeData.amount_returned === ((selectedList.amount_given || 0) - (selectedList.grand_total || 0))
                                        ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                        : 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400'
                                }`}>
                                    {completeData.amount_returned === ((selectedList.amount_given || 0) - (selectedList.grand_total || 0)) ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4" />
                                            Montant correct - Pas d'écart
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            Écart de {Math.abs(completeData.amount_returned - ((selectedList.amount_given || 0) - (selectedList.grand_total || 0))).toLocaleString()} FCFA
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="complete_notes">Notes (optionnel)</Label>
                                <Textarea
                                    id="complete_notes"
                                    value={completeData.notes}
                                    onChange={(e) => setCompleteData({ ...completeData, notes: e.target.value })}
                                    placeholder="Remarques, justification d'écart..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleCompleteList}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Terminer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ShoppingListsPageV2;
